import { useCallback, useEffect, useRef } from 'react';

/**
 * Focusable-descendant selector. `[tabindex="-1"]` is excluded on purpose: the
 * overlay container itself carries tabIndex={-1} so it can receive focus when
 * it holds no focusable children, and it must not appear in the tab ring.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Every element Tab can reach inside `container`, in document order. Also used by ImageLightbox. */
export function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * The accessibility contract shared by both overlay variants.
 *
 * Mount/unmount IS open/close — the overlay renders null when closed — so focus
 * restoration lives in an unmount cleanup rather than in an `open` effect. That
 * also guarantees the Escape listener is torn down with the overlay instead of
 * outliving it.
 */
export function useOverlayA11y(
  containerRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
): { onKeyDown: (e: React.KeyboardEvent) => void } {
  // Captured once, on mount, before focus is moved into the overlay.
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    if (container) {
      const [firstFocusable] = focusableWithin(container);
      (firstFocusable ?? container).focus();
    }

    return () => {
      // Restore focus to whatever opened us. Guarded: the opener may have been
      // removed from the document while the overlay was up.
      const opener = openerRef.current;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    };
    // containerRef is a stable ref object; this runs exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape closes. Bound to the document so it works regardless of where focus
  // sits, and removed when the overlay unmounts.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Focus trap. Only the two wrap-around cases are intercepted; a Tab in the
  // middle of the ring is left to the browser, which does it better.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const container = containerRef.current;
      if (!container) return;
      // A panel portalled from inside the overlay (e.g. a map opened from a popup) bubbles
      // its keys here through the React tree; its Tab order is its own business.
      if (!(e.target instanceof Node) || !container.contains(e.target)) return;

      const focusables = focusableWithin(container);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [containerRef],
  );

  return { onKeyDown };
}
